import type {ReactElement} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {getChatIdFromUrl, getHistory, isQuickTipsUrl} from '@/ai-chat/lib/chat-history-library';
import {aiChatContext} from './ai-chat.context';
import type {AiChatContextType, AiChatProviderProps, GeneratedImagePartActionsLoading, GeneratedImagePartActionsLoadingStatesMap} from '@/ai-chat/providers/ai-chat-provider.types';
import {AI_CHAT_SIDEBAR_EXPANDED_STATE_COOKIE} from '@/ai-chat/providers/ai-chat-provider.types';
import type {PromptBoxCoordinatorActions} from '@Components/ai-chat-window/hooks/useAiChatPromptBoxCoordinator';
import {LoadingStates} from '@Utils/loading.util';
import {readCookie, setCookie} from '@Utils/cookie.util';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {openErrorModal} from '@Modals/error-modal';
import {useGetTeamCredits} from '@Hooks/useGetTeamCredits';
import {getRemainingAiUsages} from '@Libraries/pmw-stock-media-library';
import {isUserLoggedIn} from '@Libraries/user.library';
import {useAiChatImageDownloadEvents} from '@/ai-chat/hooks/useAiChatImageDownloadEvents';
import {useAudioLimit} from '@/ai-chat/hooks/useAudioLimit';
import type {HistoryItem} from '@AiChat/ai-chat.types';
import {AI_CHAT_QUICK_TIPS_URL_SEGMENT} from '@AiChat/ai-chat.types';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';

export function AiChatProvider({children, ...props}: AiChatProviderProps): ReactElement {
  const initialChatId = getChatIdFromUrl();
  const [currentChatId, setCurrentChatIdInternal] = useState<string | undefined>(initialChatId);
  const [isQuickTipsView, setIsQuickTipsView] = useState<boolean>(isQuickTipsUrl());
  const [quickTipsReturnUrl, setQuickTipsReturnUrl] = useState<string | undefined>(undefined);
  const hasMessages = !!currentChatId;
  const isSmall = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.LARGE);
  const [isPromptBoxFocused, setIsPromptBoxFocused] = useState<boolean>(false);
  const [chatHistory, setChatHistory] = useState<HistoryItem[]>([]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [shouldFocusSearch, setShouldFocusSearch] = useState<boolean>(false);
  const recentChatHistory = chatHistory.slice(0, 5);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const teamCredits = useGetTeamCredits() ?? props.credits;
  const [remainingAiChatUsages, setRemainingAiChatUsages] = useState<number>(teamCredits);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(isUserLoggedIn());
  const [generatedImagePartActionsLoadingStatesMap, setGeneratedImagePartActionsLoadingStatesMap] = useState<GeneratedImagePartActionsLoadingStatesMap>({});
  const [isPaygUserWithAiChatAccess, setIsPaygUserWithAiChatAccess] = useState<boolean>(props.isPaygUserWithAiChatAccess);
  const [isReturningUser, setIsReturningUser] = useState<boolean>(props.isReturningUser);
  const [editingUserMessageIdMap, setEditingUserMessageIdMap] = useState<Record<string, string | null>>({});
  const [showDeletedMessages, setShowDeletedMessages] = useState<boolean>(false);
  const [isChatHistorySearchLoading, setIsChatHistorySearchLoading] = useState<boolean>(false);
  const [landingTipRefreshKey, setNewChatRefreshKey] = useState<number>(0);
  const disconnectChatStreamRef = useRef<((chatId: string) => void) | null>(null);
  const promptBoxCoordinatorRef = useRef<PromptBoxCoordinatorActions | null>(null);
  const userHasChats = props.isReturningUser;
  const [isSidebarExpanded, setIsSidebarExpandedInternal] = useState<boolean>(!isSmall && userHasChats && readCookie(AI_CHAT_SIDEBAR_EXPANDED_STATE_COOKIE) !== '0');

  const shouldRememberSidebarPreference = !isSmall && isReturningUser;
  const setIsSidebarExpanded = useCallback(
    (expanded: boolean): void => {
      setIsSidebarExpandedInternal(expanded);

      if (shouldRememberSidebarPreference) {
        setCookie(AI_CHAT_SIDEBAR_EXPANDED_STATE_COOKIE, expanded ? '1' : '0', 365);
      }
    },
    [shouldRememberSidebarPreference]
  );

  const disconnectChatStream = useCallback((chatId: string): void => {
    disconnectChatStreamRef.current?.(chatId);
  }, []);

  const registerDisconnectChatStream = useCallback((disconnectFn: (chatId: string) => void): void => {
    disconnectChatStreamRef.current = disconnectFn;
  }, []);
  const {fetchStatus: fetchAudioLimitStatus} = useAudioLimit();

  const activateForNewChat = useCallback((): void => {
    setNewChatRefreshKey((prev) => prev + 1);
    promptBoxCoordinatorRef.current?.activateForNewChat();
  }, []);

  const activatePromptBox = useCallback((): void => {
    promptBoxCoordinatorRef.current?.activatePromptBox();
  }, []);

  const registerPromptBoxCoordinator = useCallback((actions: PromptBoxCoordinatorActions | null): void => {
    promptBoxCoordinatorRef.current = actions;
  }, []);

  const defaultState: GeneratedImagePartActionsLoading = {
    downloadLoadingState: LoadingStates.NOT_LOADED,
    openEditorLoadingState: LoadingStates.NOT_LOADED,
    shareLoadingState: LoadingStates.NOT_LOADED,
    saveToMyDesignsLoadingState: LoadingStates.NOT_LOADED,
    loadingPublishingOption: null,
    publishingLoadingState: LoadingStates.NOT_LOADED,
  };

  const getLoadingStatesForImageActions = (chatId: string, imageHashedId: string): GeneratedImagePartActionsLoading => {
    return generatedImagePartActionsLoadingStatesMap[chatId]?.[imageHashedId] ?? defaultState;
  };

  const updateLoadingStatesForImageActions = (chatId: string, imageHashedId: string, updatedStates: Partial<GeneratedImagePartActionsLoading>): void => {
    setGeneratedImagePartActionsLoadingStatesMap((prev) => {
      const currentStates = prev[chatId]?.[imageHashedId] ?? defaultState;
      return {
        ...prev,
        [chatId]: {
          ...(prev[chatId] ?? {}),
          [imageHashedId]: {
            ...currentStates,
            ...updatedStates,
          },
        },
      };
    });
  };

  const fetchChatHistory = useCallback(
    async (search: string | undefined, offset = 0, limit = 20): Promise<HistoryItem[]> => {
      if (!isLoggedIn) {
        return [];
      }

      if (search !== undefined && offset === 0) {
        setIsChatHistorySearchLoading(true);
      }

      try {
        const newItems = await getHistory(search, offset, limit);

        if (offset === 0) {
          setChatHistory(newItems);
        } else {
          setChatHistory((prev) => [...prev, ...newItems]);
        }

        if (newItems.length > 0) {
          setIsReturningUser(true);
        }

        if (newItems.length < limit) {
          setHasMoreHistory(false);
        }

        return newItems;
      } catch (e) {
        console.error(e);
        openErrorModal();
        return [];
      } finally {
        if (search !== undefined && offset === 0) {
          setIsChatHistorySearchLoading(false);
        }
      }
    },
    [setChatHistory, setHasMoreHistory, openErrorModal, isLoggedIn]
  );

  const refreshRemainingAiChatInvocations = (): void => {
    getRemainingAiUsages()
      .then((res) => {
        setRemainingAiChatUsages(res);
      })
      .catch((e: unknown) => {
        console.error(e);
      });
  };

  useEffect(() => {
    refreshRemainingAiChatInvocations();
  }, []);

  useAiChatImageDownloadEvents({
    updateLoadingStatesForImageActions,
    refreshCredits: refreshRemainingAiChatInvocations,
  });

  const refetchChatHistory = async (): Promise<void> => {
    setSearchQuery(undefined);

    await fetchChatHistory(undefined, 0);
  };

  const syncStateFromUrl = useCallback((): void => {
    setIsQuickTipsView(isQuickTipsUrl());
    setCurrentChatIdInternal(getChatIdFromUrl());
  }, []);

  const getAiChatBasePath = (): string => {
    const path = window.location.pathname;
    const hasSegment = Boolean(getChatIdFromUrl()) || isQuickTipsUrl();
    return hasSegment ? path.substring(0, path.lastIndexOf('/')) : path;
  };

  const setCurrentChatId = (newChatId: string | undefined): void => {
    if (newChatId === getChatIdFromUrl() && !isQuickTipsUrl()) {
      return;
    }

    const basePath = getAiChatBasePath();
    const newUrl = newChatId ? `${basePath}/${newChatId}` : basePath;
    window.history.pushState({path: newUrl}, '', newUrl);
    syncStateFromUrl();
  };

  const navigateToQuickTips = useCallback((): void => {
    const quickTipsUrl = `${getAiChatBasePath()}/${AI_CHAT_QUICK_TIPS_URL_SEGMENT}`;
    if (!isQuickTipsUrl()) {
      setQuickTipsReturnUrl(window.location.pathname);
    }
    setIsSidebarExpandedInternal(false);
    window.history.replaceState({path: quickTipsUrl}, '', quickTipsUrl);
    syncStateFromUrl();
    trackGA4CustomEvent(GA4EventName.AICHAT_QUICK_TIPS);
  }, [syncStateFromUrl]);

  const navigateBack = useCallback((): void => {
    const returnUrl = quickTipsReturnUrl ?? window.location.pathname.replace(`/${AI_CHAT_QUICK_TIPS_URL_SEGMENT}`, '');
    window.history.replaceState({path: returnUrl}, '', returnUrl);
    setQuickTipsReturnUrl(undefined);
    syncStateFromUrl();
  }, [quickTipsReturnUrl, syncStateFromUrl]);

  useEffect(() => {
    const handleUrlChange = (): void => {
      syncStateFromUrl();
    };

    window.addEventListener('popstate', handleUrlChange);

    handleUrlChange();

    const handlePageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) {
        handleUrlChange();
        void refetchChatHistory();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return (): void => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [currentChatId]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    void refetchChatHistory();
  }, [isLoggedIn]);

  const refreshIsUserLoggedIn = (): void => {
    setIsLoggedIn(isUserLoggedIn());
  };

  const onLoginSuccessActions = (): void => {
    refreshIsUserLoggedIn();
    refreshRemainingAiChatInvocations();
    void fetchAudioLimitStatus();
  };

  const updateChatHistoryItem = useCallback((chatId: string, updates: Partial<HistoryItem>): void => {
    setChatHistory((prev) =>
      prev.map((item) => {
        if (item.idAiChat === chatId) {
          return {...item, ...updates};
        }

        return item;
      })
    );
  }, []);

  const deleteChatHistoryItem = useCallback((chatId: string): void => {
    setChatHistory((prev) => prev.filter((item) => item.idAiChat !== chatId));
  }, []);

  const setEditingUserMessageId = useCallback(
    (messageId: string | null, chatId?: string): void => {
      const idToUpdate = chatId ?? currentChatId;
      if (!idToUpdate) {
        return;
      }

      setEditingUserMessageIdMap((prev) => ({
        ...prev,
        [idToUpdate]: messageId,
      }));
    },
    [currentChatId]
  );

  const value: AiChatContextType = {
    state: {
      hasMessages,
      currentChatId: currentChatId,
      isSidebarExpanded: isSidebarExpanded,
      recentChatHistory,
      chatHistory,
      hasMoreHistory,
      isSmall: isSmall,
      isPromptBoxFocused,
      searchQuery,
      shouldFocusSearch,
      credits: remainingAiChatUsages,
      isUserLoggedIn: isLoggedIn,
      isSuperUser: props.isSuperUser,
      showDeletedMessages,
      getLoadingStatesForImageActions,
      isPaygUserWithAiChatAccess,
      editingUserMessageIdMap,
      isReturningUser,
      landingTipRefreshKey,
      isQuickTipsView,
      isChatHistorySearchLoading: isChatHistorySearchLoading,
    },
    actions: {
      setCurrentChatId: setCurrentChatId,
      setCurrentChatIdInternal: setCurrentChatIdInternal,
      setIsSidebarExpanded: setIsSidebarExpanded,
      refetchChatHistory,
      fetchChatHistory,
      setIsPromptBoxFocused,
      setSearchQuery,
      setShouldFocusSearch,
      refreshCredits: refreshRemainingAiChatInvocations,
      onLoginSuccessActions,
      updateLoadingStatesForImageActions,
      updateChatHistoryItem,
      disconnectChatStream,
      registerDisconnectChatStream,
      deleteChatHistoryItem,
      setEditingUserMessageId,
      setShowDeletedMessages,
      setIsPaygUserWithAiChatAccess,
      activateForNewChat,
      activatePromptBox,
      registerPromptBoxCoordinator,
      navigateToQuickTips,
      navigateBack,
    },
  };

  return <aiChatContext.Provider value={value}>{children}</aiChatContext.Provider>;
}
